/**
 * Jain Gaurav Yatra — Admin Bookings (read-only list).
 * Backend: GET /jain_gaurav_yatra/admin/bookings/  (cookie-session auth + X-Tenant-ID).
 */
import { extractAdminTokens, getStoredAdminSession } from "@/lib/admin-login-api";

function apiBase(): string {
  return (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "";
}

function tenantId(): string {
  return (import.meta.env.VITE_X_TENANTID as string | undefined) ?? "";
}

function jsonHeaders(): HeadersInit {
  const id = tenantId();
  const { access } = extractAdminTokens(getStoredAdminSession()?.session);
  return {
    "Content-Type": "application/json",
    ...(id ? { "X-Tenant-ID": id } : {}),
    ...(access ? { Authorization: `Bearer ${access}` } : {}),
  };
}

function errMessage(data: unknown, status: number): string {
  const d = data as Record<string, unknown>;
  const pick = (v: unknown) => (typeof v === "string" ? v : null);
  const msg = pick(d?.message) ?? pick(d?.detail);
  if (msg) {return msg;}
  try {
    return JSON.stringify(data);
  } catch {
    return `Request failed (${status})`;
  }
}

const BOOKINGS_PATH = "/jain_gaurav_yatra/admin/bookings/";

/** Page-facing booking shape (display fields resolved up front). */
export interface Booking {
  id: string;
  type: string;
  member_name: string;
  member_phone: string;
  provider_name: string;
  booking_date: string;
  booking_time: string;
  status: string;
  notes: string | null;
  group_size: number | null;
  visitor_names: string | null;
  confirmed_at: string | null;
  created_at: string;
}

function str(v: unknown): string {
  return v === null || v === undefined ? "" : String(v);
}

function strOrNull(v: unknown): string | null {
  if (v === null || v === undefined || v === "") {return null;}
  return String(v);
}

function obj(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : {};
}

/** Tolerant mapping — accepts flat (member_name) or nested (member.full_name) shapes. */
function toBooking(row: Record<string, unknown>): Booking {
  const member = obj(row.member ?? row.user);
  const volunteer = obj(row.volunteer);
  const rickshaw = obj(row.rickshaw);
  const type = str(row.type ?? row.booking_type);

  const providerName =
    str(row.provider_name) ||
    str(row.volunteer_name) ||
    str(volunteer.name) ||
    str(row.rickshaw_name) ||
    str(rickshaw.driver_name) ||
    (type === "visit" ? "Temple Visit" : "");

  const groupSizeRaw = row.group_size;
  const groupSize =
    groupSizeRaw === null || groupSizeRaw === undefined ? null : Number(groupSizeRaw) || null;

  return {
    id: str(row.id),
    type,
    member_name:
      str(row.member_name) || str(member.full_name) || str(member.name) || "Devotee",
    member_phone: str(row.member_phone) || str(member.phone) || "",
    provider_name: providerName,
    booking_date: str(row.booking_date),
    booking_time: str(row.booking_time),
    status: str(row.status),
    notes: strOrNull(row.notes),
    group_size: groupSize,
    visitor_names: strOrNull(row.visitor_names),
    confirmed_at: strOrNull(row.confirmed_at),
    created_at: str(row.created_at),
  };
}

export async function listBookings(): Promise<Booking[]> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  const res = await fetch(`${base}${BOOKINGS_PATH}`, {
    method: "GET",
    headers: jsonHeaders(),
    credentials: "include",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  const rows = Array.isArray(data)
    ? data
    : Array.isArray((data as { results?: unknown[] })?.results)
      ? (data as { results: unknown[] }).results
      : [];
  return (rows as Record<string, unknown>[]).map(toBooking);
}
